import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { dateIT } from '../lib/dateIT';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ArrowLeft, Calculator, CurrencyEur, ChartBar, Trash, Plus, Warning, Download, House, FileText, CalendarBlank, MicrosoftExcelLogo, FileCsv, Receipt, Coins, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'airbnb', label: 'Airbnb', color: '#FF5A5F' },
  { id: 'booking', label: 'Booking.com', color: '#003580' },
  { id: 'direct', label: 'Prenotazione Diretta', color: '#059669' },
  { id: 'bbit', label: 'B&B.it', color: '#E67E22' },
  { id: 'vrbo', label: 'Vrbo', color: '#3B5998' },
  { id: 'other', label: 'Altro', color: '#6B7280' },
];

const EXPENSE_CATEGORIES = [
  { id: 'luce', label: 'Luce / Energia', icon: '⚡' },
  { id: 'gas', label: 'Gas', icon: '🔥' },
  { id: 'acqua', label: 'Acqua', icon: '💧' },
  { id: 'imu', label: 'IMU', icon: '🏛️' },
  { id: 'condominio_ordinario', label: 'Condominio Ordinario', icon: '🏢' },
  { id: 'condominio_straordinario', label: 'Condominio Straordinario', icon: '🔧' },
  { id: 'tari', label: 'TARI', icon: '🗑️' },
  { id: 'rai_siae', label: 'RAI / SIAE', icon: '📺' },
  { id: 'lavanderia', label: 'Lavanderia', icon: '👕' },
  { id: 'pulizie', label: 'Pulizie', icon: '🧹' },
  { id: 'materiale_consumo', label: 'Materiale Consumo', icon: '🧴' },
  { id: 'capsule_caffe', label: 'Capsule Caffè', icon: '☕' },
  { id: 'altro', label: 'Altro', icon: '📦' },
];

const PIE_COLORS = ['#FF5A5F','#003580','#059669','#E67E22','#3B5998','#6B7280','#8B5CF6','#EC4899','#F59E0B','#10B981','#6366F1','#EF4444','#14B8A6'];

// ==================== CALCULATOR COMPONENT ====================
// PlatformCalculator removed — replaced by simpler payout-centric flow.
// Legacy bookings collection kept untouched; migration endpoint available.


// ==================== PAYOUTS COMPONENT ====================
function PayoutsPanel({ payouts, onAdd, onDelete, properties = [], propertyFilter = 'all', propertyById = () => null }) {
  const [platform, setPlatform] = useState('airbnb');
  const [grossAmount, setGrossAmount] = useState('');
  const [taxRate, setTaxRate] = useState(21); // only used when platform === 'direct'
  const [payoutDate, setPayoutDate] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [notes, setNotes] = useState('');
  const [propertyId, setPropertyId] = useState('');

  const isDirect = platform === 'direct';

  // Parse comma/dot decimals; locale-italian aware
  const parseEur = (s) => {
    if (!s) return 0;
    const n = parseFloat(String(s).replace(/[^\d.,-]/g, '').replace(',', '.'));
    return isNaN(n) ? 0 : n;
  };
  const grossNumeric = parseEur(grossAmount);
  // Preview math (mirrors backend formula in _compute_payout_amounts)
  const previewNet = isDirect ? +(grossNumeric * (1 - taxRate / 100)).toFixed(2) : grossNumeric;
  const previewTax = isDirect ? +(grossNumeric * (taxRate / 100)).toFixed(2) : 0;

  // When the user filters by a specific property, preselect it in the form
  useEffect(() => {
    if (propertyFilter !== 'all') setPropertyId(propertyFilter);
  }, [propertyFilter]);

  const handleAdd = async () => {
    if (!grossNumeric || grossNumeric <= 0) { toast.error('Importo bonifico obbligatorio'); return; }
    if (!payoutDate) { toast.error('Data del bonifico obbligatoria'); return; }
    await onAdd({
      platform,
      gross_amount: grossNumeric,
      tax_rate: isDirect ? taxRate : 0,
      payout_date: payoutDate,
      invoice_ref: invoiceRef,
      period_from: periodFrom,
      period_to: periodTo,
      notes,
      property_id: propertyId || null,
    });
    setGrossAmount(''); setInvoiceRef(''); setPeriodFrom(''); setPeriodTo(''); setNotes('');
    toast.success(isDirect ? `Diretta registrata: netto €${previewNet}, cedolare €${previewTax}` : `Bonifico OTA registrato: €${grossNumeric}`);
  };

  const totalNet = payouts.reduce((s, p) => s + (p.net_amount || 0), 0);
  const totalTax = payouts.reduce((s, p) => s + (p.tax_amount || 0), 0);
  const platformLabel = (id) => PLATFORMS.find(p => p.id === id)?.label || id;
  const platformColor = (id) => PLATFORMS.find(p => p.id === id)?.color || '#71717a';

  return (
    <div className="space-y-4" data-testid="payouts-panel">
      {/* Info banner */}
      <div className="bg-emerald-50 border border-emerald-200 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-emerald-600 flex items-center justify-center flex-shrink-0">
            <Receipt size={16} weight="bold" className="text-white" />
          </div>
          <div>
            <p className="text-sm font-black text-emerald-900 mb-1">Inserisci l&apos;importo del bonifico, ai calcoli pensiamo noi</p>
            <p className="text-xs text-emerald-800 leading-relaxed">
              • <strong>OTA</strong> (Airbnb, Booking, Vrbo…): il bonifico è già al netto — la piattaforma trattiene le tasse alla fonte.
              <br/>• <strong>Prenotazione diretta</strong>: l&apos;importo è lordo, scegli la cedolare (21% prima casa / 26% seconda+) e calcoliamo netto e tasse da versare.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white border border-zinc-200 p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-zinc-900 flex items-center justify-center">
            <Plus size={14} weight="bold" className="text-white" />
          </div>
          <h3 className="text-base font-black text-zinc-900 tracking-tight">Registra un bonifico</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Platform selector */}
          <div className="sm:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Piattaforma *</Label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
              {PLATFORMS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlatform(p.id)}
                  className={`text-[11px] font-bold uppercase tracking-wider px-2 py-2 border transition-all ${
                    platform === p.id
                      ? 'bg-zinc-900 text-white border-zinc-900'
                      : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500'
                  }`}
                  data-testid={`payout-platform-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Direct: cedolare picker */}
          {isDirect && (
            <div className="sm:col-span-2 bg-amber-50 border border-amber-200 p-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-amber-900 mb-2 block">Regime fiscale (cedolare secca)</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTaxRate(21)}
                  className={`px-3 py-2.5 border text-xs font-bold text-left transition-all ${taxRate === 21 ? 'bg-amber-700 text-white border-amber-700' : 'bg-white text-zinc-700 border-zinc-300'}`}
                  data-testid="payout-rate-21"
                >
                  21% — Prima casa
                </button>
                <button
                  type="button"
                  onClick={() => setTaxRate(26)}
                  className={`px-3 py-2.5 border text-xs font-bold text-left transition-all ${taxRate === 26 ? 'bg-amber-700 text-white border-amber-700' : 'bg-white text-zinc-700 border-zinc-300'}`}
                  data-testid="payout-rate-26"
                >
                  26% — Dal 2° immobile
                </button>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">
              {isDirect ? 'Importo bonifico (lordo) (€) *' : 'Importo bonificato (€) *'}
            </Label>
            <Input
              type="text"
              inputMode="decimal"
              placeholder={isDirect ? "es. 1000,00" : "es. 1250,50"}
              value={grossAmount}
              onChange={(e) => setGrossAmount(e.target.value)}
              className="rounded-none border-zinc-300 text-lg font-bold tabular-nums"
              data-testid="payout-amount-input"
            />
            {isDirect && grossNumeric > 0 && (
              <div className="mt-2 bg-zinc-50 border border-zinc-200 p-2 grid grid-cols-2 gap-2 text-xs" data-testid="payout-preview">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">Netto in tasca</p>
                  <p className="text-base font-black text-emerald-700 tabular-nums">€ {previewNet.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">Cedolare {taxRate}%</p>
                  <p className="text-base font-black text-amber-700 tabular-nums">€ {previewTax.toFixed(2)}</p>
                </div>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Data bonifico *</Label>
            <Input
              type="date"
              value={payoutDate}
              onChange={(e) => setPayoutDate(e.target.value)}
              className="rounded-none border-zinc-300"
              data-testid="payout-date-input"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">N° fattura / riferimento</Label>
            <Input
              placeholder="es. BK-2026-0042"
              value={invoiceRef}
              onChange={(e) => setInvoiceRef(e.target.value)}
              className="rounded-none border-zinc-300"
              data-testid="payout-invoice-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Periodo dal</Label>
              <Input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} className="rounded-none border-zinc-300 text-xs" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">al</Label>
              <Input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} className="rounded-none border-zinc-300 text-xs" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Note</Label>
            <Input
              placeholder="es. Bonifico Booking aprile 2026"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-none border-zinc-300"
            />
          </div>
          {properties.length > 0 && (
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">
                Immobile {properties.length > 1 ? '(opzionale)' : ''}
              </Label>
              <select
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 bg-white text-sm rounded-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none"
                data-testid="payout-property-select"
              >
                <option value="">— Nessun immobile specifico —</option>
                {properties.map(p => (
                  <option key={p.id || p._id} value={p.id || p._id}>{p.name} · {p.property_city}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <Button
          onClick={handleAdd}
          className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-wider text-xs mt-4 w-full sm:w-auto"
          data-testid="payout-save-btn"
        >
          <Plus size={14} className="mr-2" /> Aggiungi a guadagni
        </Button>
      </div>

      {/* Totale */}
      {payouts.length > 0 && (
        <div className="bg-zinc-900 text-white p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1">Totale netto</p>
            <p className="text-2xl font-black text-emerald-400 tabular-nums">€ {totalNet.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-[10px] text-zinc-400 mt-1">{payouts.length} {payouts.length === 1 ? 'movimento' : 'movimenti'}</p>
          </div>
          {totalTax > 0 && (
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-1">Cedolare da versare</p>
              <p className="text-2xl font-black text-amber-400 tabular-nums">€ {totalTax.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-[10px] text-zinc-400 mt-1">Solo da prenotazioni dirette</p>
            </div>
          )}
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400 mb-1">Lordo registrato</p>
            <p className="text-2xl font-black text-zinc-300 tabular-nums">€ {(totalNet + totalTax).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      {/* Lista payouts */}
      {payouts.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center">
          <p className="text-sm text-zinc-500">Nessun bonifico registrato ancora. Inserisci il primo qui sopra.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 divide-y divide-zinc-100">
          {payouts.map((p) => (
            <div key={p.id} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-zinc-50 transition-colors" data-testid={`payout-row-${p.id}`}>
              <div
                className="w-1.5 self-stretch flex-shrink-0"
                style={{ backgroundColor: platformColor(p.platform) }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500">{platformLabel(p.platform)}</span>
                  {p.invoice_ref && (
                    <span className="text-[10px] font-bold text-zinc-400">· {p.invoice_ref}</span>
                  )}
                  {p.payout_date && (
                    <span className="text-[10px] text-zinc-400">· {dateIT(p.payout_date)}</span>
                  )}
                </div>
                {(p.period_from || p.period_to) && (
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    Periodo: {p.period_from ? dateIT(p.period_from) : '—'} → {p.period_to ? dateIT(p.period_to) : '—'}
                  </p>
                )}
                {p.property_id && propertyById(p.property_id) && (
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    🏠 {propertyById(p.property_id).name}
                  </p>
                )}
                {p.notes && <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{p.notes}</p>}
              </div>
              <p className="text-base sm:text-lg font-black text-emerald-700 tabular-nums whitespace-nowrap">
                € {(p.net_amount || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <button
                onClick={() => onDelete(p.id)}
                className="text-zinc-400 hover:text-red-600 transition-colors flex-shrink-0"
                data-testid={`payout-delete-${p.id}`}
                aria-label="Elimina bonifico"
              >
                <Trash size={16} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExpensesPanel({ expenses, onAdd, onDelete, properties = [], propertyFilter = 'all', propertyById = () => null }) {
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [propertyId, setPropertyId] = useState('');

  useEffect(() => {
    if (propertyFilter !== 'all') setPropertyId(propertyFilter);
  }, [propertyFilter]);

  const handleAdd = async () => {
    if (!category || !amount) { toast.error('Categoria e importo obbligatori'); return; }
    await onAdd({ category, amount: parseFloat(amount), description, date, month: date ? date.slice(0, 7) : '', property_id: propertyId || null });
    setAmount(''); setDescription(''); setDate('');
    toast.success('Spesa aggiunta!');
  };

  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const catLabel = (id) => EXPENSE_CATEGORIES.find(c => c.id === id)?.label || id;
  const catIcon = (id) => EXPENSE_CATEGORIES.find(c => c.id === id)?.icon || '📦';

  return (
    <div className="space-y-6" data-testid="expenses-panel">
      <div className="bg-white border border-zinc-200 p-5 sm:p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Aggiungi spesa</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-none" data-testid="expense-category-select"><SelectValue placeholder="Categoria..." /></SelectTrigger>
            <SelectContent>
              {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.icon} {c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Importo (€)" className="rounded-none" data-testid="expense-amount-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrizione (opzionale)" className="rounded-none" />
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="rounded-none" />
        </div>
        {properties.length > 0 && (
          <div className="mb-3">
            <select
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              className="w-full h-10 px-3 border border-zinc-300 bg-white text-sm rounded-none focus:border-zinc-900 outline-none"
              data-testid="expense-property-select"
            >
              <option value="">— Spesa generica (non per immobile specifico) —</option>
              {properties.map(p => (
                <option key={p.id || p._id} value={p.id || p._id}>🏠 {p.name} · {p.property_city}</option>
              ))}
            </select>
          </div>
        )}
        <Button onClick={handleAdd} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-sm" data-testid="add-expense-btn">
          <Plus size={16} className="mr-1" /> Aggiungi
        </Button>
      </div>

      {expenses.length > 0 && (
        <div className="bg-white border border-zinc-200 p-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Spese registrate ({expenses.length})</p>
            <p className="text-lg font-black text-red-600">€ {totalExpenses.toFixed(2)}</p>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {expenses.map(e => (
              <div key={e.id} className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-100 group hover:border-zinc-300 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{catIcon(e.category)}</span>
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{catLabel(e.category)}</p>
                    {e.description && <p className="text-xs text-zinc-400">{e.description}</p>}
                    {e.property_id && propertyById(e.property_id) && (
                      <p className="text-[11px] text-zinc-500 mt-0.5">🏠 {propertyById(e.property_id).name}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-sm text-zinc-900">€ {(e.amount || 0).toFixed(2)}</span>
                  <button onClick={() => onDelete(e.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50" data-testid={`delete-expense-${e.id}`}>
                    <Trash size={16} className="text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== CHARTS COMPONENT ====================
function ChartsPanel({ summary }) {
  if (!summary) return null;
  const { by_platform = {}, by_month = {}, by_category = {} } = summary;

  const platformData = Object.entries(by_platform).map(([k, v]) => ({
    name: PLATFORMS.find(p => p.id === k)?.label || k,
    lordo: v.gross, netto: v.net, commissioni: v.commission, tasse: v.tax, count: v.count,
    color: PLATFORMS.find(p => p.id === k)?.color || '#999',
  }));

  const monthData = Object.entries(by_month).map(([k, v]) => ({
    month: k, entrate: v.gross, netto: v.net, spese: v.expenses, prenotazioni: v.bookings,
  }));

  const categoryData = Object.entries(by_category).map(([k, v], i) => ({
    name: EXPENSE_CATEGORIES.find(c => c.id === k)?.label || k,
    value: v, color: PIE_COLORS[i % PIE_COLORS.length],
  }));

  return (
    <div className="space-y-6" data-testid="charts-panel">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Lordo Totale', value: summary.total_gross, color: 'text-zinc-900' },
          { label: 'Netto Reale', value: summary.total_net, color: 'text-emerald-600' },
          { label: 'Spese Totali', value: summary.total_expenses, color: 'text-red-600' },
          { label: 'Profitto', value: summary.profit, color: summary.profit >= 0 ? 'text-emerald-600' : 'text-red-600' },
        ].map((kpi, i) => (
          <div key={i} className="bg-white border border-zinc-200 p-4 text-center">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">{kpi.label}</p>
            <p className={`text-xl sm:text-2xl font-black ${kpi.color}`}>€ {(kpi.value || 0).toFixed(2)}</p>
          </div>
        ))}
      </div>

      {/* Monthly Revenue Chart */}
      {monthData.length > 0 && (
        <div className="bg-white border border-zinc-200 p-5 sm:p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Andamento mensile</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `€ ${v.toFixed(2)}`} />
              <Legend />
              <Bar dataKey="entrate" name="Lordo" fill="#18181b" radius={[4,4,0,0]} />
              <Bar dataKey="netto" name="Netto" fill="#059669" radius={[4,4,0,0]} />
              <Bar dataKey="spese" name="Spese" fill="#EF4444" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Platform Breakdown */}
      {platformData.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="bg-white border border-zinc-200 p-5 sm:p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Entrate per piattaforma</p>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={platformData} dataKey="lordo" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {platformData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => `€ ${v.toFixed(2)}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {categoryData.length > 0 && (
            <div className="bg-white border border-zinc-200 p-5 sm:p-6">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Spese per categoria</p>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {categoryData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `€ ${v.toFixed(2)}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Profit Trend */}
      {monthData.length > 1 && (
        <div className="bg-white border border-zinc-200 p-5 sm:p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Trend profitto mensile</p>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={monthData.map(m => ({ ...m, profitto: m.netto - m.spese }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `€ ${v.toFixed(2)}`} />
              <Line type="monotone" dataKey="profitto" name="Profitto" stroke="#059669" strokeWidth={3} dot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Bookings count */}
      <div className="bg-white border border-zinc-200 p-5 sm:p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Dettaglio piattaforme</p>
        <div className="space-y-2">
          {platformData.map((p, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-zinc-50">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                <span className="text-sm font-medium">{p.name}</span>
                <span className="text-xs text-zinc-400">({p.count} prenotazioni)</span>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold">€ {p.netto.toFixed(2)}</p>
                <p className="text-xs text-zinc-400">lordo € {p.lordo.toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== ACCOUNTANT REPORT DIALOG ====================
function AccountantReportDialog({ open, onOpenChange, directBookingsCount }) {
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const maxDays = 731; // 2 anni
  const diffDays = dateFrom && dateTo ? differenceInDays(dateTo, dateFrom) : 0;
  const exceedsLimit = diffDays > maxDays;
  const invalidOrder = dateFrom && dateTo && dateTo < dateFrom;

  const handleQuickRange = (months) => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - months);
    setDateFrom(from);
    setDateTo(to);
  };

  const handleQuickYear = (yearOffset) => {
    const year = new Date().getFullYear() + yearOffset;
    setDateFrom(new Date(year, 0, 1));
    setDateTo(new Date(year, 11, 31));
  };

  const handleDownload = async () => {
    if (!dateFrom || !dateTo) { toast.error('Seleziona entrambe le date'); return; }
    if (invalidOrder) { toast.error('La data di inizio deve precedere quella di fine'); return; }
    if (exceedsLimit) { toast.error('Periodo massimo: 2 anni'); return; }

    setDownloading(true);
    try {
      const from = format(dateFrom, 'yyyy-MM-dd');
      const to = format(dateTo, 'yyyy-MM-dd');
      const res = await axios.get(
        `${API}/accounting/accountant-report/pdf?date_from=${from}&date_to=${to}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_commercialista_${from}_${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report scaricato!');
      onOpenChange(false);
      setDateFrom(null); setDateTo(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore durante la generazione';
      toast.error(typeof msg === 'string' ? msg : 'Errore durante la generazione');
    }
    setDownloading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none border-zinc-300" data-testid="accountant-report-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black tracking-tight">Report Commercialista</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Esporta un PDF dettagliato delle <strong>prenotazioni dirette</strong> (non OTA) con calcolo di ritenuta d&apos;acconto/cedolare secca.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
            <p className="font-bold mb-1">Cosa include il PDF?</p>
            <ul className="list-disc list-inside space-y-0.5 text-[11px]">
              <li>Solo prenotazioni con piattaforma &quot;Prenotazione Diretta&quot; o &quot;Altro&quot;</li>
              <li>Dati contribuente (nome, CF, residenza)</li>
              <li>Riepilogo fiscale: lordo, imponibile, ritenuta, netto</li>
              <li>Dettaglio prenotazione-per-prenotazione con date, ospite, importi</li>
            </ul>
          </div>

          {/* Quick ranges */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Periodi rapidi</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button onClick={() => handleQuickYear(0)} className="text-xs border border-zinc-300 px-2 py-1.5 hover:bg-zinc-900 hover:text-white transition-colors font-semibold" data-testid="quick-current-year">
                {new Date().getFullYear()}
              </button>
              <button onClick={() => handleQuickYear(-1)} className="text-xs border border-zinc-300 px-2 py-1.5 hover:bg-zinc-900 hover:text-white transition-colors font-semibold" data-testid="quick-prev-year">
                {new Date().getFullYear() - 1}
              </button>
              <button onClick={() => handleQuickRange(6)} className="text-xs border border-zinc-300 px-2 py-1.5 hover:bg-zinc-900 hover:text-white transition-colors font-semibold" data-testid="quick-6m">
                Ultimi 6 mesi
              </button>
              <button onClick={() => handleQuickRange(12)} className="text-xs border border-zinc-300 px-2 py-1.5 hover:bg-zinc-900 hover:text-white transition-colors font-semibold" data-testid="quick-12m">
                Ultimi 12 mesi
              </button>
            </div>
          </div>

          {/* Date pickers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Dal</Label>
              <Popover open={fromOpen} onOpenChange={setFromOpen}>
                <PopoverTrigger asChild>
                  <button className={`w-full flex items-center gap-2 border border-zinc-300 px-3 py-2.5 text-left text-sm transition-colors hover:border-zinc-500 ${dateFrom ? 'text-zinc-900 font-medium' : 'text-zinc-400'}`} data-testid="date-from-trigger">
                    <CalendarBlank size={16} weight="bold" className="text-zinc-400 flex-shrink-0" />
                    {dateFrom ? format(dateFrom, 'd MMMM yyyy', { locale: it }) : 'Seleziona data'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none border-zinc-300" align="start">
                  <Calendar mode="single" selected={dateFrom} onSelect={(d) => { setDateFrom(d); setFromOpen(false); }} locale={it} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Al</Label>
              <Popover open={toOpen} onOpenChange={setToOpen}>
                <PopoverTrigger asChild>
                  <button className={`w-full flex items-center gap-2 border border-zinc-300 px-3 py-2.5 text-left text-sm transition-colors hover:border-zinc-500 ${dateTo ? 'text-zinc-900 font-medium' : 'text-zinc-400'}`} data-testid="date-to-trigger">
                    <CalendarBlank size={16} weight="bold" className="text-zinc-400 flex-shrink-0" />
                    {dateTo ? format(dateTo, 'd MMMM yyyy', { locale: it }) : 'Seleziona data'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none border-zinc-300" align="start">
                  <Calendar mode="single" selected={dateTo} onSelect={(d) => { setDateTo(d); setToOpen(false); }} locale={it} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Validation messages */}
          {dateFrom && dateTo && !invalidOrder && !exceedsLimit && (
            <div className="text-xs text-zinc-500 flex items-center justify-between border-t border-zinc-100 pt-3">
              <span>Periodo selezionato: <strong className="text-zinc-900">{diffDays + 1} giorni</strong></span>
              <span className="text-zinc-400">max 731</span>
            </div>
          )}
          {invalidOrder && (
            <div className="bg-red-50 border border-red-200 p-2 text-xs text-red-800">La data di inizio deve precedere quella di fine.</div>
          )}
          {exceedsLimit && (
            <div className="bg-red-50 border border-red-200 p-2 text-xs text-red-800">Periodo massimo consentito: 2 anni (731 giorni). Hai selezionato {diffDays} giorni.</div>
          )}

          <p className="text-[11px] text-zinc-400 leading-relaxed">
            Prenotazioni dirette registrate in contabilità: <strong className="text-zinc-900">{directBookingsCount}</strong>. Solo quelle nel periodo selezionato verranno incluse.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="cancel-report-btn">Annulla</Button>
          <Button
            onClick={handleDownload}
            disabled={!dateFrom || !dateTo || invalidOrder || exceedsLimit || downloading}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
            data-testid="download-report-btn"
          >
            <Download size={16} className="mr-2" /> {downloading ? 'Generazione...' : 'Scarica PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== EXPORT COMPLETO DIALOG (Excel/CSV) ====================
function ExportAllDialog({ open, onOpenChange, totalBookings, totalExpenses }) {
  const [format_, setFormat] = useState('excel'); // 'excel' | 'csv'
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const invalidOrder = dateFrom && dateTo && dateFrom > dateTo;

  const handleQuickYear = (offset) => {
    const y = new Date().getFullYear() + offset;
    setDateFrom(new Date(y, 0, 1));
    setDateTo(new Date(y, 11, 31));
  };
  const handleAll = () => { setDateFrom(null); setDateTo(null); };

  const handleDownload = async () => {
    if (invalidOrder) { toast.error('La data di inizio deve precedere quella di fine'); return; }

    setDownloading(true);
    try {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('date_from', format(dateFrom, 'yyyy-MM-dd'));
      if (dateTo) qs.set('date_to', format(dateTo, 'yyyy-MM-dd'));

      const endpoint = format_ === 'excel' ? 'excel' : 'csv';
      const res = await axios.get(
        `${API}/accounting/export/${endpoint}${qs.toString() ? '?' + qs.toString() : ''}`,
        { withCredentials: true, responseType: 'blob' }
      );

      const mime = format_ === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/zip';
      const ext = format_ === 'excel' ? 'xlsx' : 'zip';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = (dateFrom && dateTo)
        ? `${format(dateFrom, 'yyyy-MM-dd')}_${format(dateTo, 'yyyy-MM-dd')}`
        : 'completo';
      a.download = `contabilita_${suffix}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format_.toUpperCase()} scaricato!`);
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore durante l\'export';
      toast.error(typeof msg === 'string' ? msg : 'Errore durante l\'export');
    }
    setDownloading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none border-zinc-300" data-testid="export-all-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black tracking-tight">Export contabilità</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Esporta <strong>tutte</strong> le prenotazioni (tutte le piattaforme) e le spese in Excel o CSV.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Format selector */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Formato</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setFormat('excel')}
                className={`group flex items-center gap-2.5 border px-3 py-3 transition-all ${format_ === 'excel' ? 'border-emerald-600 bg-emerald-50' : 'border-zinc-300 bg-white hover:border-zinc-500'}`}
                data-testid="format-excel-btn"
              >
                <MicrosoftExcelLogo size={22} weight="fill" className={format_ === 'excel' ? 'text-emerald-600' : 'text-zinc-400 group-hover:text-zinc-700'} />
                <div className="text-left">
                  <p className={`text-sm font-bold ${format_ === 'excel' ? 'text-emerald-900' : 'text-zinc-900'}`}>Excel</p>
                  <p className="text-[10px] text-zinc-500">.xlsx · 3 fogli</p>
                </div>
              </button>
              <button
                onClick={() => setFormat('csv')}
                className={`group flex items-center gap-2.5 border px-3 py-3 transition-all ${format_ === 'csv' ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-300 bg-white hover:border-zinc-500'}`}
                data-testid="format-csv-btn"
              >
                <FileCsv size={22} weight="fill" className={format_ === 'csv' ? 'text-white' : 'text-zinc-400 group-hover:text-zinc-700'} />
                <div className="text-left">
                  <p className={`text-sm font-bold ${format_ === 'csv' ? 'text-white' : 'text-zinc-900'}`}>CSV</p>
                  <p className={`text-[10px] ${format_ === 'csv' ? 'text-zinc-400' : 'text-zinc-500'}`}>.zip · 2 file</p>
                </div>
              </button>
            </div>
          </div>

          {/* Quick ranges */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Periodi rapidi</Label>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={handleAll} className={`text-xs border border-zinc-300 px-2 py-1.5 hover:bg-zinc-900 hover:text-white transition-colors font-semibold ${!dateFrom && !dateTo ? 'bg-zinc-900 text-white' : ''}`} data-testid="quick-all">
                Tutto
              </button>
              <button onClick={() => handleQuickYear(0)} className="text-xs border border-zinc-300 px-2 py-1.5 hover:bg-zinc-900 hover:text-white transition-colors font-semibold" data-testid="export-quick-current-year">
                {new Date().getFullYear()}
              </button>
              <button onClick={() => handleQuickYear(-1)} className="text-xs border border-zinc-300 px-2 py-1.5 hover:bg-zinc-900 hover:text-white transition-colors font-semibold" data-testid="export-quick-prev-year">
                {new Date().getFullYear() - 1}
              </button>
            </div>
          </div>

          {/* Date pickers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Dal</Label>
              <Popover open={fromOpen} onOpenChange={setFromOpen}>
                <PopoverTrigger asChild>
                  <button className={`w-full flex items-center gap-2 border border-zinc-300 px-3 py-2.5 text-left text-sm transition-colors hover:border-zinc-500 ${dateFrom ? 'text-zinc-900 font-medium' : 'text-zinc-400'}`} data-testid="export-date-from-trigger">
                    <CalendarBlank size={16} weight="bold" className="text-zinc-400 flex-shrink-0" />
                    {dateFrom ? format(dateFrom, 'd MMMM yyyy', { locale: it }) : 'Tutto'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none border-zinc-300" align="start">
                  <Calendar mode="single" selected={dateFrom} onSelect={(d) => { setDateFrom(d); setFromOpen(false); }} locale={it} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Al</Label>
              <Popover open={toOpen} onOpenChange={setToOpen}>
                <PopoverTrigger asChild>
                  <button className={`w-full flex items-center gap-2 border border-zinc-300 px-3 py-2.5 text-left text-sm transition-colors hover:border-zinc-500 ${dateTo ? 'text-zinc-900 font-medium' : 'text-zinc-400'}`} data-testid="export-date-to-trigger">
                    <CalendarBlank size={16} weight="bold" className="text-zinc-400 flex-shrink-0" />
                    {dateTo ? format(dateTo, 'd MMMM yyyy', { locale: it }) : 'Oggi'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none border-zinc-300" align="start">
                  <Calendar mode="single" selected={dateTo} onSelect={(d) => { setDateTo(d); setToOpen(false); }} locale={it} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {invalidOrder && (
            <div className="bg-red-50 border border-red-200 p-2 text-xs text-red-800">La data di inizio deve precedere quella di fine.</div>
          )}

          <div className="bg-zinc-50 border border-zinc-200 p-3 text-[11px] text-zinc-600 leading-relaxed">
            <p className="mb-1"><strong className="text-zinc-900">Dati totali disponibili:</strong> {totalBookings} prenotazioni · {totalExpenses} spese</p>
            {format_ === 'excel' ? (
              <p>Excel contiene 3 fogli: <strong>Prenotazioni</strong> (dettaglio riga-per-riga), <strong>Spese</strong> e <strong>Riepilogo</strong> aggregato per piattaforma + KPI.</p>
            ) : (
              <p>CSV compresso in ZIP con <strong>prenotazioni.csv</strong> + <strong>spese.csv</strong> (UTF-8 con BOM, separatore <code className="bg-white px-1 border border-zinc-200">;</code>, virgola decimale — Excel italiano friendly).</p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="cancel-export-btn">Annulla</Button>
          <Button
            onClick={handleDownload}
            disabled={invalidOrder || downloading}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
            data-testid="download-export-btn"
          >
            <Download size={16} className="mr-2" /> {downloading ? 'Generazione...' : `Scarica ${format_.toUpperCase()}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAIN PAGE ====================
export default function ContabilitaPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('bonifico');
  const [reportOpen, setReportOpen] = useState(false);
  const [exportAllOpen, setExportAllOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState('all'); // 'all' or property id

  const directBookingsCount = bookings.filter(b => b.platform === 'direct' || b.platform === 'other').length;

  const fetchData = useCallback(async () => {
    try {
      const [bRes, eRes, pRes, sRes, propRes] = await Promise.all([
        axios.get(`${API}/accounting/bookings`, { withCredentials: true }),
        axios.get(`${API}/accounting/expenses`, { withCredentials: true }),
        axios.get(`${API}/accounting/payouts`, { withCredentials: true }),
        axios.get(`${API}/accounting/summary`, { withCredentials: true }),
        axios.get(`${API}/properties`, { withCredentials: true }),
      ]);
      setBookings(bRes.data);
      setExpenses(eRes.data);
      setPayouts(pRes.data);
      setSummary(sRes.data);
      setProperties(propRes.data || []);
    } catch (err) {
      console.error('Fetch accounting error:', err);
    }
  }, []);

  // Filtered views by property
  const filteredPayouts = propertyFilter === 'all' ? payouts : payouts.filter(p => p.property_id === propertyFilter);
  const filteredExpenses = propertyFilter === 'all' ? expenses : expenses.filter(e => e.property_id === propertyFilter);
  const filteredBookings = propertyFilter === 'all' ? bookings : bookings.filter(b => b.property_id === propertyFilter);
  const multiProperty = properties.length > 1;
  const propertyById = (id) => properties.find(p => p.id === id || p._id === id);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addExpense = async (data) => {
    await axios.post(`${API}/accounting/expenses`, data, { withCredentials: true });
    fetchData();
  };

  const deleteExpense = async (id) => {
    await axios.delete(`${API}/accounting/expenses/${id}`, { withCredentials: true });
    fetchData();
  };

  const deleteBooking = async (id) => {
    await axios.delete(`${API}/accounting/bookings/${id}`, { withCredentials: true });
    fetchData();
  };

  const addPayout = async (data) => {
    await axios.post(`${API}/accounting/payouts`, data, { withCredentials: true });
    fetchData();
  };

  const deletePayout = async (id) => {
    await axios.delete(`${API}/accounting/payouts/${id}`, { withCredentials: true });
    fetchData();
  };

  const [migrating, setMigrating] = useState(false);
  const migrateLegacy = async () => {
    setMigrating(true);
    try {
      const r = await axios.post(`${API}/accounting/migrate-bookings-to-payouts`, {}, { withCredentials: true });
      toast.success(`${r.data.migrated} prenotazioni vecchie migrate come bonifici`);
      fetchData();
    } catch (e) {
      toast.error('Errore migrazione');
    } finally {
      setMigrating(false);
    }
  };
  // Show migration banner if user has legacy bookings AND not all are already migrated
  const hasLegacy = bookings.length > 0;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="contabilita-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-zinc-100 transition-colors" data-testid="back-button">
            <ArrowLeft size={24} weight="bold" className="text-zinc-700" />
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-zinc-900 tracking-tight">Contabilità</h1>
            <p className="text-xs text-zinc-400">Calcola i tuoi guadagni reali</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Ricevute quick access — 2 cards side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="ricevute-shortcuts">
          <button
            onClick={() => navigate('/receipts')}
            className="group bg-white border border-zinc-200 rounded-2xl p-4 sm:p-5 text-left flex items-center gap-4 hover:border-zinc-400 hover:shadow-md hover:-translate-y-0.5 transition-all"
            data-testid="shortcut-ricevute-affitto"
          >
            <div className="w-11 h-11 bg-zinc-900 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-zinc-800 transition-colors">
              <Receipt size={20} weight="fill" className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Ricevute</p>
              <p className="text-sm font-bold text-zinc-900">Affitto Breve</p>
              <p className="text-[11px] text-zinc-500">Con marca da bollo</p>
            </div>
            <CaretRight size={18} className="text-zinc-300 group-hover:text-zinc-900 flex-shrink-0" />
          </button>
          <button
            onClick={() => navigate('/ricevute-imposta-soggiorno')}
            className="group bg-white border border-zinc-200 rounded-2xl p-4 sm:p-5 text-left flex items-center gap-4 hover:border-teal-400 hover:shadow-md hover:-translate-y-0.5 transition-all"
            data-testid="shortcut-ricevute-imposta"
          >
            <div className="w-11 h-11 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-teal-700 transition-colors">
              <Coins size={20} weight="fill" className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-teal-700">Ricevute</p>
              <p className="text-sm font-bold text-zinc-900">Imposta di Soggiorno</p>
              <p className="text-[11px] text-zinc-500">Ospiti × Notti × Tariffa</p>
            </div>
            <CaretRight size={18} className="text-zinc-300 group-hover:text-teal-600 flex-shrink-0" />
          </button>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border-2 border-amber-300 p-4 sm:p-5 mb-6 flex items-start gap-3" data-testid="disclaimer-banner">
          <Warning size={24} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-amber-900 text-sm">Disclaimer importante</h3>
            <p className="text-amber-800 text-xs mt-1 leading-relaxed">I calcoli forniti hanno valore puramente <strong>indicativo e informativo</strong>. I risultati possono leggermente differire dai valori reali a causa di arrotondamenti, variazioni nelle commissioni o aggiornamenti normativi. Consulta sempre il tuo commercialista o CAF per la dichiarazione dei redditi.</p>
          </div>
        </div>

        {/* Migration banner: only when legacy bookings exist */}
        {hasLegacy && (
          <div className="bg-violet-50 border-2 border-violet-300 p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="migration-banner">
            <div className="flex-1">
              <h3 className="font-bold text-violet-900 text-sm">Hai {bookings.length} prenotazione/i dal vecchio Calcolatore</h3>
              <p className="text-violet-800 text-xs mt-1 leading-relaxed">
                Il Calcolatore è stato sostituito. Migra i dati esistenti in &quot;Entrate&quot; così entrano nei nuovi calcoli e nei grafici. <strong>L&apos;operazione non elimina nulla</strong>.
              </p>
            </div>
            <Button
              onClick={migrateLegacy}
              disabled={migrating}
              className="rounded-none bg-violet-700 text-white text-xs font-black uppercase tracking-wider px-4 py-2.5 hover:bg-violet-800"
              data-testid="migrate-btn"
            >
              {migrating ? 'Migrazione...' : 'Migra ora'}
            </Button>
          </div>
        )}

        {/* Property filter — visible only if user has > 1 property */}
        {multiProperty && (
          <div className="bg-white border border-zinc-200 p-3 sm:p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid="property-filter-bar">
            <div className="flex items-center gap-2">
              <House size={16} weight="bold" className="text-zinc-700 flex-shrink-0" />
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-600">Filtra per immobile</p>
            </div>
            <select
              value={propertyFilter}
              onChange={(e) => setPropertyFilter(e.target.value)}
              className="flex-1 h-9 px-3 border border-zinc-300 bg-white text-sm focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none"
              data-testid="property-filter-select"
            >
              <option value="all">Tutti gli immobili ({properties.length}) — totale aggregato</option>
              {properties.map(p => (
                <option key={p.id || p._id} value={p.id || p._id}>{p.name} · {p.property_city}</option>
              ))}
            </select>
            {propertyFilter !== 'all' && (
              <button
                onClick={() => setPropertyFilter('all')}
                className="text-[10px] font-black uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors flex-shrink-0"
                data-testid="property-filter-clear"
              >
                ✕ Rimuovi filtro
              </button>
            )}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full bg-zinc-100 rounded-none h-auto p-1 mb-6">
            <TabsTrigger value="bonifico" className="rounded-none text-[10px] sm:text-sm py-2.5 font-bold data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-bonifico">
              <Receipt size={16} className="mr-1 hidden sm:inline" /> Entrate
            </TabsTrigger>
            <TabsTrigger value="spese" className="rounded-none text-[10px] sm:text-sm py-2.5 font-bold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-spese">
              <CurrencyEur size={16} className="mr-1 hidden sm:inline" /> Spese
            </TabsTrigger>
            <TabsTrigger value="grafici" className="rounded-none text-[10px] sm:text-sm py-2.5 font-bold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-grafici">
              <ChartBar size={16} className="mr-1 hidden sm:inline" /> Grafici
            </TabsTrigger>
            <TabsTrigger value="storico" className="rounded-none text-[10px] sm:text-sm py-2.5 font-bold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-storico">
              <House size={16} className="mr-1 hidden sm:inline" /> Storico
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bonifico">
            <PayoutsPanel payouts={filteredPayouts} onAdd={addPayout} onDelete={deletePayout} properties={properties} propertyFilter={propertyFilter} propertyById={propertyById} />
          </TabsContent>

          <TabsContent value="spese">
            <ExpensesPanel expenses={filteredExpenses} onAdd={addExpense} onDelete={deleteExpense} properties={properties} propertyFilter={propertyFilter} propertyById={propertyById} />
          </TabsContent>

          <TabsContent value="grafici">
            <ChartsPanel summary={summary} />
          </TabsContent>

          <TabsContent value="storico">
            {/* Export CTAs — 2 cards side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {/* Commercialista Report CTA */}
              <div className="bg-zinc-900 text-white p-5 sm:p-6 flex flex-col justify-between gap-4" data-testid="commercialista-cta">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <FileText size={18} weight="bold" className="text-emerald-400" />
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400">Per il commercialista</p>
                  </div>
                  <p className="text-sm font-bold text-white mb-0.5">Report prenotazioni dirette</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    PDF con ritenuta/cedolare, solo non-OTA. Max 2 anni.
                  </p>
                </div>
                <Button
                  onClick={() => setReportOpen(true)}
                  className="rounded-none bg-white text-zinc-900 hover:bg-zinc-100 font-bold uppercase tracking-wider text-xs self-start"
                  data-testid="open-report-dialog-btn"
                >
                  <Download size={16} className="mr-2" /> Scarica PDF
                </Button>
              </div>

              {/* Export completo CTA */}
              <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-5 sm:p-6 flex flex-col justify-between gap-4" data-testid="export-all-cta">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <MicrosoftExcelLogo size={18} weight="fill" className="text-emerald-200" />
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-200">Export completo</p>
                  </div>
                  <p className="text-sm font-bold text-white mb-0.5">Tutte le piattaforme</p>
                  <p className="text-xs text-emerald-100 leading-relaxed">
                    Excel (3 fogli) o CSV. Prenotazioni + spese, tutti i canali.
                  </p>
                </div>
                <Button
                  onClick={() => setExportAllOpen(true)}
                  className="rounded-none bg-white text-emerald-900 hover:bg-emerald-50 font-bold uppercase tracking-wider text-xs self-start"
                  data-testid="open-export-all-dialog-btn"
                >
                  <Download size={16} className="mr-2" /> Esporta Excel/CSV
                </Button>
              </div>
            </div>

            <div className="space-y-3" data-testid="storico-panel">
              {filteredBookings.length === 0 ? (
                <div className="bg-white border border-zinc-200 p-8 text-center">
                  <p className="text-zinc-400">{propertyFilter === 'all' ? 'Nessuna prenotazione registrata' : 'Nessuna prenotazione per questo immobile'}</p>
                </div>
              ) : (
                filteredBookings.map(b => (
                  <div key={b.id} className="bg-white border border-zinc-200 p-4 flex items-center justify-between group hover:border-zinc-400 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PLATFORMS.find(p => p.id === b.platform)?.color || '#999' }} />
                      <div>
                        <p className="text-sm font-bold text-zinc-900">{PLATFORMS.find(p => p.id === b.platform)?.label || b.platform}</p>
                        <p className="text-xs text-zinc-400">
                          {b.guest_name && `${b.guest_name} · `}{b.check_in && `${dateIT(b.check_in)} → ${dateIT(b.check_out)}`}
                          {b.property_id && propertyById(b.property_id) && ` · ${propertyById(b.property_id).name}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-black text-emerald-600">€ {(b.net_amount || 0).toFixed(2)}</p>
                        <p className="text-xs text-zinc-400">lordo € {(b.gross_amount || 0).toFixed(2)}</p>
                      </div>
                      <button onClick={() => deleteBooking(b.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50">
                        <Trash size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <AccountantReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        directBookingsCount={directBookingsCount}
      />

      <ExportAllDialog
        open={exportAllOpen}
        onOpenChange={setExportAllOpen}
        totalBookings={bookings.length}
        totalExpenses={expenses.length}
      />
    </div>
  );
}
